/**
 * PostComments — list + add-comment form rendered beneath a post.
 *
 * Behavior:
 *   - Public visitors can read approved comments. The new-comment form
 *     is hidden when not authenticated.
 *   - Pre-moderation tenants: a member's submitted comment shows a
 *     "pending review" notice and lands in the staff approval queue.
 *     Post-moderation tenants: comment appears immediately.
 *   - Staff (admin / has edit_posts) see pending + hidden comments
 *     too, with Approve / Hide buttons inline.
 *   - Authors can hide their own comments (soft-delete).
 */

import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  PostComment,
  listPostComments,
  createPostComment,
  approvePostComment,
  hidePostComment,
} from "../../api/postsAPI";
import { selectIsAdmin, selectIsStaff } from "../../store/slices/authSlice";
import { useTheme } from "../../context/ThemeContext";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import {
  getCardBackground,
  getPrimaryText,
  getSecondaryText,
  getMutedText,
  getLightBorder,
  getInputStyles,
} from "../../utils/themeUtils";
import { safeGetItem } from "@mediatechgroup/app-version";

interface Props {
  postId: string;
  enabled: boolean;
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

const PostComments: React.FC<Props> = ({ postId, enabled }) => {
  const { isDarkMode } = useTheme();
  const { confirm, confirmWithInput, confirmModal } = useConfirmModal();
  const token = safeGetItem("authToken");
  const isAdmin = useSelector(selectIsAdmin);
  const isStaff = useSelector(selectIsStaff);
  const isStaffLevel = isAdmin || isStaff;

  const [comments, setComments] = useState<PostComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [body, setBody] = useState("");

  const reload = async () => {
    const res = await listPostComments(token, postId);
    if (res.success) setComments(res.data || []);
  };

  useEffect(() => {
    if (!enabled || !postId) return;
    let cancelled = false;
    (async () => {
      await reload();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, enabled]);

  if (!enabled) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.info("Sign in to comment");
      return;
    }
    const text = body.trim();
    if (!text) return;
    setSubmitting(true);
    const res = await createPostComment(token, postId, text);
    setSubmitting(false);
    if (res.success) {
      setBody("");
      toast.success(res.message || "Comment posted");
      await reload();
    } else {
      toast.error(res.error || "Failed to post comment");
    }
  };

  const handleApprove = async (commentId: string) => {
    if (!token) return;
    const res = await approvePostComment(token, postId, commentId);
    if (res.success) {
      await reload();
    } else {
      toast.error(res.error || "Failed to approve");
    }
  };

  const handleHide = async (commentId: string, currentlyMine: boolean) => {
    if (!token) return;
    let reason: string | null = null;
    if (currentlyMine) {
      if (!(await confirm({
        title: "Delete comment?",
        message: "Are you sure you want to delete this comment?",
        confirmText: "Delete",
      }))) return;
    } else {
      // Moderation path — single modal collects the optional log reason.
      reason = await confirmWithInput({
        title: "Hide comment?",
        message: "Are you sure you want to hide this comment?",
        confirmText: "Hide",
        input: { label: "Reason (optional, shown to moderation log)" },
      });
      if (reason === null) return;
    }
    const res = await hidePostComment(token, postId, commentId, reason || undefined);
    if (res.success) {
      await reload();
    } else {
      toast.error(res.error || "Failed to hide");
    }
  };

  const visibleComments = comments.filter((c) =>
    isStaffLevel ? true : c.status === "approved"
  );
  const pendingCount = comments.filter((c) => c.status === "pending").length;

  return (
    <section className="mt-8">
      {confirmModal}
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <h3 className={`text-lg font-semibold ${getPrimaryText(isDarkMode)}`}>
          Comments {comments.length > 0 && <span className={getMutedText(isDarkMode)}>({comments.length})</span>}
        </h3>
        {isStaffLevel && pendingCount > 0 && (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
            {pendingCount} awaiting approval
          </span>
        )}
      </div>

      {/* New-comment form — auth required */}
      {token ? (
        <form
          onSubmit={handleSubmit}
          className={`p-3 rounded-lg border ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)} mb-4`}
        >
          <textarea
            aria-label="Comment"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Share your thoughts…"
            rows={3}
            className={`w-full p-2 rounded-md text-sm ${getInputStyles(isDarkMode)}`}
          />
          <div className="flex items-center justify-end gap-2 mt-2">
            <button
              type="submit"
              disabled={submitting || !body.trim()}
              className="px-4 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50"
            >
              {submitting ? "Posting…" : "Post comment"}
            </button>
          </div>
        </form>
      ) : (
        <div
          className={`p-3 rounded-lg border ${getLightBorder(isDarkMode)} ${getCardBackground(isDarkMode)} mb-4 text-sm italic ${getMutedText(isDarkMode)}`}
        >
          Sign in to leave a comment.
        </div>
      )}

      {loading ? (
        <p className={`text-sm ${getMutedText(isDarkMode)}`}>Loading comments…</p>
      ) : visibleComments.length === 0 ? (
        <p className={`text-sm italic ${getMutedText(isDarkMode)}`}>
          No comments yet. Be the first.
        </p>
      ) : (
        <ul className={`divide-y ${isDarkMode ? "divide-gray-700" : "divide-gray-200"}`}>
          {visibleComments.map((c) => {
            const mine =
              !!token &&
              ((c.author_user_id && c.author_user_id) || c.author_membership_id);
            // Author resolution from the optimistic-name we stored at insert time
            const authorLabel = c.author_name || c.author_email || "Member";
            return (
              <li
                key={c.id}
                className={`py-3 ${c.status === "pending" ? "bg-amber-50/50 dark:bg-amber-900/10 -mx-3 px-3 rounded" : ""} ${
                  c.status === "hidden" ? "opacity-50" : ""
                }`}
              >
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${getPrimaryText(isDarkMode)}`}>{authorLabel}</span>
                    <span className={`text-xs ${getMutedText(isDarkMode)}`}>{relativeTime(c.created_at)}</span>
                    {c.status === "pending" && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
                        Pending
                      </span>
                    )}
                    {c.status === "hidden" && (
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200">
                        Hidden
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {isStaffLevel && c.status === "pending" && (
                      <button
                        onClick={() => handleApprove(c.id)}
                        className="text-xs px-2 py-0.5 rounded bg-green-600 hover:bg-green-700 text-white"
                      >
                        Approve
                      </button>
                    )}
                    {(isStaffLevel || mine) && c.status !== "hidden" && (
                      <button
                        onClick={() => handleHide(c.id, !!mine && !isStaffLevel)}
                        className={`text-xs ${getMutedText(isDarkMode)} hover:text-red-500`}
                      >
                        {mine && !isStaffLevel ? "Delete" : "Hide"}
                      </button>
                    )}
                  </div>
                </div>
                <p className={`text-sm whitespace-pre-wrap ${getSecondaryText(isDarkMode)}`}>{c.body}</p>
                {c.status === "hidden" && c.hidden_reason && (
                  <p className={`text-xs italic mt-1 ${getMutedText(isDarkMode)}`}>
                    Hidden: {c.hidden_reason}
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default PostComments;
